import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { feedbackPrompt } from 'network/ApiAxios';

const ChatContext = createContext();

export const useChatContext = () => useContext(ChatContext);

// Give the user time to read the answer before asking what they thought of it.
const FEEDBACK_PROMPT_DELAY_MS = 10000;

export const ChatProvider = ({ children }) => {
    // Bumped whenever the conversation list may have changed (new chat,
    // generated title, regenerate); the sidebar refetches on each change.
    const [conversationsVersion, setConversationsVersion] = useState(0);
    const [isFeedbackPromptVisible, setIsFeedbackPromptVisible] = useState(false);

    // Lives here rather than in useChat because a brand-new chat navigates to
    // its new id once the first answer finishes, which would drop hook state.
    // The backend decides whether to ask (admin setting, already answered,
    // shown too often); these refs only stop us asking twice per page visit.
    const feedbackCheckInFlightRef = useRef(false);
    const feedbackPromptFinishedRef = useRef(false);
    const feedbackTimerRef = useRef(null);

    useEffect(() => () => clearTimeout(feedbackTimerRef.current), []);

    const refreshConversations = useCallback(() => {
        setConversationsVersion(version => version + 1);
    }, []);

    // Called by useChat each time an answer finishes streaming successfully.
    const notifyAnswerComplete = useCallback(async () => {
        if (feedbackPromptFinishedRef.current || feedbackCheckInFlightRef.current) return;
        feedbackCheckInFlightRef.current = true;
        try {
            const { show, check_again: checkAgain } = await feedbackPrompt();
            if (show) {
                feedbackPromptFinishedRef.current = true;
                feedbackTimerRef.current = setTimeout(() => {
                    setIsFeedbackPromptVisible(true);
                    feedbackPrompt('post', { action: 'shown' }).catch(() => {});
                }, FEEDBACK_PROMPT_DELAY_MS);
            } else if (!checkAgain) {
                feedbackPromptFinishedRef.current = true;
            }
        } catch (error) {
            // Feedback is optional - never let it get in the way of chatting.
        } finally {
            feedbackCheckInFlightRef.current = false;
        }
    }, []);

    const hideFeedbackPrompt = useCallback(() => {
        setIsFeedbackPromptVisible(false);
    }, []);

    return (
        <ChatContext.Provider value={{
            conversationsVersion,
            refreshConversations,
            isFeedbackPromptVisible,
            notifyAnswerComplete,
            hideFeedbackPrompt,
        }}>
            {children}
        </ChatContext.Provider>
    );
};
